import React, { useState } from 'react';
import {
	TextField,
	FormControl,
	InputLabel,
	Select,
	MenuItem,
	FormControlLabel,
	Switch,
	IconButton,
	SelectChangeEvent,
} from '@mui/material';
import Grid from '@mui/material/Grid2';
import AddCircleIcon from '@mui/icons-material/AddCircle';
import DeleteIcon from '@mui/icons-material/Delete';
import type { FormData, wholesale as Wholesale } from '../../../types';

const WholesaleFields: React.FC<{
	formData: FormData;
	setFormData: React.Dispatch<React.SetStateAction<FormData>>;
	disabled?: boolean;
}> = ({ formData, setFormData, disabled }) => {
	const [customizedField, setCustomizedField] = useState<Wholesale>({
		type: 'text',
		label: '',
		required: false,
	});
	const [error, setError] = useState(false);

	const handleAddWholeSaleField = () => {
		if (customizedField.label.trim().length === 0) {
			setError(true);
			return;
		}

		setFormData((prev) => ({
			...prev,
			wholesaleFields: [...prev.wholesaleFields, customizedField],
		}));

		setCustomizedField({ type: 'text', label: '', required: false });
		setError(false);
	};

	const handleDeleteWholeSaleField = (index: number) => {
		setFormData((prev) => ({
			...prev,
			wholesaleFields: prev.wholesaleFields.filter((_, i) => i !== index),
		}));
	};

	const handleWholeSaleFieldChange = (
		index: number,
		field: keyof Wholesale,
		value: string | boolean
	) => {
		const newWholesaleFields = [...formData.wholesaleFields];
		newWholesaleFields[index] = {
			...newWholesaleFields[index],
			[field]: value,
		};
		setFormData((prev) => ({ ...prev, wholesaleFields: newWholesaleFields }));
	};

	const handleSelectChange =
		(index: number, field: keyof Wholesale) => (e: SelectChangeEvent<string>) => {
			handleWholeSaleFieldChange(index, field, e.target.value);
		};

	const handleNewSelectChange = (field: keyof Wholesale) => (e: SelectChangeEvent<string>) => {
		setCustomizedField((prev) => ({
			...prev,
			[field]: e.target.value,
		}));
	};

	return (
		<>
			<Grid size={{ xs: 12 }}>
				<TextField
					sx={{ margin: '0px' }}
					size="small"
					label="Wholesale Minimum Order Quantity"
					name="wholesaleMinQuantity"
					type="number"
					value={formData.wholesaleMinQuantity}
					onChange={(e) =>
						setFormData((prev) => ({
							...prev,
							wholesaleMinQuantity: Number(e.target.value),
						}))
					}
					fullWidth
					margin="normal"
					inputProps={{ min: '1' }}
					disabled={disabled}
				/>
			</Grid>
			<Grid size={{ xs: 12 }}>
				<FormControlLabel
					sx={{
						margin: 0,
						'& .MuiFormControlLabel-label': {
							fontSize: { xs: '14px', sm: '16px', md: '18px' }, // responsive font size
						},
					}}
					disabled={disabled}
					control={
						<Switch
							checked={formData.isCustomized}
							onChange={(e) =>
								setFormData((prev) => ({
									...prev,
									isCustomized: e.target.checked,
								}))
							}
						/>
					}
					label="Is Customized"
				/>
			</Grid>

			{formData.isCustomized &&
				formData.wholesaleFields.length > 0 &&
				formData.wholesaleFields.map((value, index) => (
					<React.Fragment key={index}>
						<Grid size={{ xs: 12, md: 4 }}>
							<FormControl fullWidth disabled={disabled}>
								<InputLabel id={`wholesale-type-${index}`}>Field Type</InputLabel>
								<Select
									size="small"
									labelId={`wholesale-type-${index}`}
									id={`wholesale-type-id-${index}`}
									value={value.type}
									label="Field Type"
									onChange={handleSelectChange(index, 'type')}
								>
									<MenuItem value="text" sx={{ xs: 14, sm: 16, md: 18 }}>
										Text
									</MenuItem>
									<MenuItem value="image" sx={{ xs: 14, sm: 16, md: 18 }}>
										Image
									</MenuItem>
								</Select>
							</FormControl>
						</Grid>
						<Grid size={{ xs: 12, md: 4 }}>
							<TextField
								disabled={disabled}
								sx={{ margin: '0px' }}
								size="small"
								label="Label"
								name="label"
								type="text"
								value={value.label}
								onChange={(e) =>
									handleWholeSaleFieldChange(index, 'label', e.target.value)
								}
								required
								fullWidth
								margin="normal"
							/>
						</Grid>
						<Grid size={{ xs: 9, sm: 9, md: 3 }}>
							<FormControlLabel
								disabled={disabled}
								sx={{
									margin: 0,
									'& .MuiFormControlLabel-label': {
										fontSize: { xs: '14px', sm: '16px', md: '18px' }, // responsive font size
									},
								}}
								control={
									<Switch
										checked={value.required}
										onChange={(e) =>
											handleWholeSaleFieldChange(
												index,
												'required',
												e.target.checked
											)
										}
									/>
								}
								label="Is Required"
							/>
						</Grid>
						<Grid size={{ xs: 3, sm: 3, md: 1 }}>
							<IconButton
								disabled={disabled}
								aria-label="Delete"
								onClick={() => handleDeleteWholeSaleField(index)}
							>
								<DeleteIcon />
							</IconButton>
						</Grid>
					</React.Fragment>
				))}

			{formData.isCustomized && (
				<React.Fragment>
					<Grid size={{ xs: 12, sm: 6, md: 4 }}>
						<FormControl fullWidth disabled={disabled}>
							<InputLabel id={`wholesale-type`}>Field Type</InputLabel>
							<Select
								size="small"
								labelId={`wholesale-type`}
								id={`wholesale-type-id`}
								value={customizedField.type}
								label="Field Type"
								onChange={handleNewSelectChange('type')}
							>
								<MenuItem value="text">Text</MenuItem>
								<MenuItem value="image">Image</MenuItem>
							</Select>
						</FormControl>
					</Grid>
					<Grid size={{ xs: 12, sm: 6, md: 4 }}>
						<TextField
							disabled={disabled}
							sx={{
								margin: 0,
								'& .MuiFormControlLabel-label': {
									fontSize: { xs: '14px', sm: '16px', md: '18px' }, // responsive font size
								},
							}}
							size="small"
							label="Label"
							name="label"
							type="text"
							value={customizedField.label}
							onChange={(e) => {
								setCustomizedField((prev) => ({ ...prev, label: e.target.value }));
								if (error && e.target.value.trim() !== '') {
									setError(false);
								}
							}}
							fullWidth
							margin="normal"
							error={error}
							helperText={error ? 'Label is required' : ''}
						/>
					</Grid>
					<Grid size={{ xs: 9, sm: 9, md: 3 }}>
						<FormControlLabel
							disabled={disabled}
							sx={{
								margin: 0,
								'& .MuiFormControlLabel-label': {
									fontSize: { xs: '14px', sm: '16px', md: '18px' }, // responsive font size
								},
							}}
							control={
								<Switch
									checked={customizedField.required}
									onChange={(e) =>
										setCustomizedField((prev) => ({
											...prev,
											required: e.target.checked,
										}))
									}
								/>
							}
							label="Is Required"
						/>
					</Grid>
					<Grid size={{ xs: 3, sm: 3, md: 1 }}>
						<IconButton
							aria-label="Add"
							onClick={handleAddWholeSaleField}
							disabled={disabled}
						>
							<AddCircleIcon />
						</IconButton>
					</Grid>
				</React.Fragment>
			)}
		</>
	);
};

export default WholesaleFields;
